import { Cell, Expander, ExpanderContent, ExpanderHeader, Grid, Toggle, ToggleGroup } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { usePageTitle } from '../../app/PageTitleContext'
import { toLocaleString } from '../../../util/DateUtil'
import { UserLink, PersonLink, MrnLink } from '../../app/Links'
import DataView from '../../common/DataView'
import { CtrContext } from '../CtrContext'
import { getPidFromIdentifier } from '../../../util/StringUtil'

export default function CtrChanges() {
  usePageView({ page_detail: 'DMConsole:CtrChanges' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.ctr.changes'))
  const ctrContext = useContext(CtrContext)
  const ctrChangesList = ctrContext?.ctrChangesList
  const [toggle, setToggle] = useState(true)
  return (
    <>
      {ctrChangesList && ctrChangesList.length === 0 && t('noChangesFound', `No changes found`)}
      {ctrChangesList && ctrChangesList.length > 0 && (
        <>
          <ToggleGroup>
            <Toggle
              label={t('ctr.openAll', 'Open All')}
              name="allOpen"
              checked={toggle}
              onChange={({ target }) => setToggle(target.checked)}
            />
          </ToggleGroup>
          <Grid id="CTR Changes">
            {ctrChangesList.map((ctrChange) => {
              return <CtrChange ctrChange={ctrChange} toggle={toggle} key={v4()} />
            })}
          </Grid>
        </>
      )}
    </>
  )
}

function CtrChange({ ctrChange, toggle }) {
  const [open, setOpen] = useState()
  const ctrChangeSet = ctrChange.content.gedcomx.changeSets[0]
  const ctrPersons = ctrChange.content.gedcomx.persons

  useEffect(() => {
    setOpen(toggle)
  }, [toggle, setOpen])

  return (
    <Cell columns={12}>
      <div
        style={{
          borderRadius: '5px',
          padding: '20px',
          paddingTop: '8px',
          boxShadow: `0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)`,
        }}
      >
        <Expander open={open} setOpen={setOpen}>
          <ExpanderHeader>
            <CtrChangeSetHeader ctrChangeSet={ctrChangeSet} />
          </ExpanderHeader>
          <ExpanderContent>
            <div style={{ marginTop: '8px' }}>
              <Grid>
                <Cell columns={12}>
                  <CtrChangeSetInfo ctrChangeSet={ctrChangeSet} />
                </Cell>
                <Cell columns={12}>
                  <CtrChangeTable ctrChangeSet={ctrChangeSet} ctrPersons={ctrPersons} />
                </Cell>
              </Grid>
            </div>
          </ExpanderContent>
        </Expander>
      </div>
    </Cell>
  )
}

function CtrChangeSetHeader({ ctrChangeSet }) {
  const { t } = useTranslation()
  return (
    <Grid guttersY="none">
      <Cell columns={4}>
        <div>
          <strong>{t('changes.dateChanged', 'Change Date')}: </strong>{' '}
          {toLocaleString(new Date(ctrChangeSet.createDate.original), true)}
        </div>
      </Cell>
      <Cell columns={4}>
        <div>
          <strong>{t('changes.modifiedBy', 'Modified By')}: </strong>
          {ctrChangeSet.changeSource.sourceWhoUsysId && <UserLink userId={ctrChangeSet.changeSource.sourceWhoUsysId} />}
          {ctrChangeSet.changeSource.sourceWhoMrn && <MrnLink mrn={ctrChangeSet.changeSource.sourceWhoMrn} />}
          {!ctrChangeSet.changeSource.sourceWhoUsysId &&
            !ctrChangeSet.changeSource.sourceWhoMrn &&
            t('changes.na', 'N/A')}
        </div>
      </Cell>
      <Cell columns={4}>
        <div>
          <strong>{t('changes.changeReason', 'Change Reason')}: </strong>
          {ctrChangeSet.changeSource.reasonText === '' || ctrChangeSet.changeSource.reasonText === null
            ? `(${t('ctr.noReasonGiven', 'No reason given')})`
            : ctrChangeSet.changeSource.reasonText}
        </div>
      </Cell>
    </Grid>
  )
}

function CtrChangeSetInfo({ ctrChangeSet }) {
  const { t } = useTranslation()
  const systemTypeArray = ctrChangeSet?.changeSource?.systemType?.split('/')
  const reasonTypeArray = ctrChangeSet?.changeSource?.reasonType?.split('/')
  return (
    <Grid>
      <Cell columns={2}>
        {systemTypeArray && (
          <div>
            <strong>{t('changes.systemType', 'System Type')}: </strong>
            {systemTypeArray[systemTypeArray.length - 1]}
          </div>
        )}
      </Cell>
      <p columns={6} />
      <Cell columns={4}>
        {reasonTypeArray && (
          <div>
            <strong>{t('reasonType', 'Reason Type')}: </strong>
            {t(
              `ctr.editReasonType.${reasonTypeArray[reasonTypeArray.length - 1]}`,
              reasonTypeArray[reasonTypeArray.length - 1].replace(/_/g, ' ')
            )}
          </div>
        )}
      </Cell>
    </Grid>
  )
}

function CtrChangeTable({ ctrChangeSet, ctrPersons }) {
  const { t } = useTranslation()

  function keysToLabelsMapper(keyValues, whatChangedResourceReferenceId) {
    keyValues.forEach((keyValuePair) => {
      switch (keyValuePair.label) {
        case 'changeType':
          keyValuePair.label = t('changeType', 'Change Type')
          if (keyValuePair.detail === 'ARCHIVE') {
            keyValues.push(getCtrNameFormTypeAndValue(whatChangedResourceReferenceId, true))
          }
          break
        case 'changeFieldType':
          keyValuePair.label = t('ctr.changeFieldType', 'Change Field Type')
          break
        case 'personInfoFieldType':
          keyValuePair.label = t('ctr.personInfoField', 'Person Info Field Type')
          break
        case 'eventFieldType':
          keyValuePair.label = t('ctr.eventFieldType', 'Event Field Type')
          break
        case 'oldValue':
          keyValuePair.label = t('ctr.oldValue', 'Old Value')
          keyValuePair.detail = t(`ctr.statusCode.${keyValuePair.detail}`, keyValuePair.detail)
          break
        case 'newValue':
          keyValuePair.label = t('ctr.newValue', 'New Value')
          keyValuePair.detail = t(`ctr.statusCode.${keyValuePair.detail}`, keyValuePair.detail)
          break
        case 'nameFormFieldType': {
          const nameFormTypeAndValue = getCtrNameFormTypeAndValue(whatChangedResourceReferenceId)
          keyValuePair.label = nameFormTypeAndValue.label
          keyValuePair.detail = nameFormTypeAndValue.detail
          break
        }
        default:
          break
      }
    })
  }

  function getCtrNameFormTypeAndValue(whatChangedResourceReferenceId, changeTypeIsArchived = false) {
    const nameFormTypeAndValue = {
      label: null,
      detail: null,
    }
    const person = ctrPersons.find((ctrPerson) => whatChangedResourceReferenceId === ctrPerson.id)
    if (changeTypeIsArchived) {
      const usysId = getPidFromIdentifier(person.identifiers)
      nameFormTypeAndValue.label = t('person.label', 'Person')
      nameFormTypeAndValue.detail = (
        <>
          {`${person?.names?.[0]?.nameForms?.[0]?.fullText} `}
          (<PersonLink personId={usysId} />)
        </>
      )
      return nameFormTypeAndValue
    }
    const nameType = person?.names?.[0]?.nameForms?.[1]?.parts?.[0]?.type?.split('/')
    nameFormTypeAndValue.label = nameType && nameType[nameType.length - 1]
    nameFormTypeAndValue.detail = person?.names?.[0]?.nameForms?.[1]?.parts?.[0]?.value?.trim()
    return nameFormTypeAndValue
  }

  return (
    <Grid>
      {ctrChangeSet?.changes?.map((change) => {
        const keyValues = Object.keys(change)
          .filter((key) => {
            return typeof change[key] === 'string'
          })
          .map((key) => {
            let detail = change[key]
            if (change[key].includes('http')) {
              detail = detail.split('/')
              detail = detail[detail.length - 1]
            }
            return { label: key, detail }
          })
        let whatChangedResourceReferenceId = null
        if (change.whatChangedResourceReference) {
          whatChangedResourceReferenceId = change.whatChangedResourceReference.resourceId
        }
        keysToLabelsMapper(keyValues, whatChangedResourceReferenceId)
        return (
          <Cell columns={12} key={v4()}>
            <DataView data={keyValues} />
          </Cell>
        )
      })}
    </Grid>
  )
}
